import dayjs from "dayjs";
import ImageViewer from "components/ImageViewer";
import CountryFlag from "components/CountryFlag";
import SvgIcon from "components/base/SvgIcon";
import {
  prepareListViewLocation,
  prepareListViewRate,
} from "components/UserCardList/helpers/userCard";
import placeholderPicture from "images/placeholderProfile.png";

/**
 * Relative activity label in the viewer's local timezone.
 * dayjs parses ISO/UTC instants and compares against local now/calendar days.
 */
const formatLastActivity = (value) => {
  if (!value) {
    return null;
  }

  const date = dayjs(value);
  const now = dayjs();

  if (!date.isValid() || date.isAfter(now)) {
    return null;
  }

  if (date.isSame(now, "day")) {
    const hours = now.diff(date, "hour");

    if (hours < 1) {
      const minutes = Math.max(1, now.diff(date, "minute"));
      return minutes === 1 ? "1 minute ago" : `${minutes} minutes ago`;
    }

    return hours === 1 ? "1 hour ago" : `${hours} hours ago`;
  }

  if (date.isSame(now.subtract(1, "day"), "day")) {
    return "yesterday";
  }

  const calendarDays = now.startOf("day").diff(date.startOf("day"), "day");

  if (calendarDays < 7) {
    return calendarDays === 1 ? "1 day ago" : `${calendarDays} days ago`;
  }

  if (calendarDays < 30) {
    const weeks = Math.floor(calendarDays / 7);
    return weeks === 1 ? "1 week ago" : `${weeks} weeks ago`;
  }

  const months = Math.max(1, now.diff(date, "month"));
  return months === 1 ? "1 month ago" : `${months} months ago`;
};

const UserIdentity = ({ user }) => {
  const userName =
    user.fullName ||
    `${user.given_name || ""} ${user.family_name || ""}`.trim();
  const role =
    user.careers?.[0]?.title || user.rankDetails?.content?.current_role || "";
  const locationDisplay =
    user.listViewLocation ||
    prepareListViewLocation(user.location, user.locale);
  const rateDisplay = user.listViewRate || prepareListViewRate(user);
  const activitySource = user.lastActivityDate || user.createdAt;
  const activityLabel = formatLastActivity(activitySource);
  const activityTitle = activitySource
    ? dayjs(activitySource).format("MMM D, YYYY h:mm A")
    : undefined;

  return (
    <div className="flex items-center gap-3">
      <div className="h-11 w-11 shrink-0 overflow-hidden rounded-lg bg-gray-100">
        <ImageViewer
          objectKey={user.headshotKey}
          placeholder={
            <img
              src={placeholderPicture}
              alt=""
              className="h-full w-full object-cover"
            />
          }
          className="h-full w-full object-cover"
        />
      </div>

      <div className="flex min-w-0 flex-1 flex-col gap-0.5">
        <div className="flex min-w-0 items-center gap-1.5 font-inter text-sm text-slate-700">
          <span className="truncate font-semibold">{userName || "—"}</span>
          {role ? (
            <>
              <span className="shrink-0 text-slate-400">·</span>
              <span className="truncate font-normal text-slate-500">
                {role}
              </span>
            </>
          ) : null}
        </div>

        <div className="flex min-w-0 items-center gap-2 font-inter text-xs text-slate-500">
          <div className="flex min-w-0 items-center gap-1 truncate">
            {user.location?.countryCode ? (
              <CountryFlag
                country={user.location.countryCode.toLowerCase()}
                size="16x12"
                className="h-3 w-4 shrink-0"
              />
            ) : null}
            <span className="truncate">{locationDisplay}</span>
          </div>
          {rateDisplay ? <span className="shrink-0">{rateDisplay}</span> : null}
        </div>
      </div>

      {activityLabel ? (
        <span
          className="flex shrink-0 items-center gap-1 font-inter text-xs font-normal text-sky-500"
          title={activityTitle}
        >
          <SvgIcon
            type="checkCircleFill"
            className="h-3 w-3 shrink-0 text-sky-500"
          />
          Activity {activityLabel}
        </span>
      ) : null}
    </div>
  );
};

export default UserIdentity;
